import { PALO_COLUMN_TYPES, PaloColumnType } from './palo-column-type';
import { PaloColumnVariant } from './palo-column-variant';

/** What a column says about its own measure; PaloColumn carries all of it. */
export interface PaloColumnMeasure {
    type?: PaloColumnType;
    /** A width the list set itself, e.g. '40%' — it outranks the kind. */
    width?: string;
    align?: 'left' | 'center' | 'right';
    variant?: PaloColumnVariant;
}

/**
 * Whether this column lives off what the fixed ones leave over — a name, a
 * title, a partner. Only a kind that declares no width of its own does, and
 * only while the list has not been given one at the column.
 */
export const paloColumnIsFlexible = (column: PaloColumnMeasure): boolean => {
    const kind = column.type ? PALO_COLUMN_TYPES[column.type] : undefined;

    return !column.width && !!kind && undefined === kind.width;
};

/** '120px' as 120; a share such as '40%' has no fixed measure. */
const pixelsOf = (width: string): number | undefined => {
    const match = /^(\d+(?:\.\d+)?)px$/.exec(width.trim());

    return match ? Number(match[1]) : undefined;
};

export const paloColumnLayout = (
    column: PaloColumnMeasure,
    /** Equal share of the leftover room, for the flexible columns. */
    share?: string
): { width?: string; minWidth?: number; align?: 'left' | 'center' | 'right'; variant?: PaloColumnVariant } => {
    const kind = column.type ? PALO_COLUMN_TYPES[column.type] : undefined;

    return {
        width: column.width ?? (kind?.width ? `${kind.width}px` : paloColumnIsFlexible(column) ? share : undefined),
        // A table laid out by its content treats a width as a wish and takes
        // the room back for whatever column has the most text. A fixed kind
        // keeps its measure through the floor as well: the status pill stayed
        // at 114 of its 180 pixels otherwise. A width the list set itself
        // in pixels is its floor instead of the kind's; a share has none.
        minWidth: column.width ? pixelsOf(column.width) : kind?.minWidth ?? kind?.width,
        align: column.align ?? kind?.align,
        variant: column.variant ?? kind?.variant,
    };
};
